/**
 * Indian tax & duty rate tables for calculators.
 * All rates sourced from state government websites as of early 2026.
 * These are approximate — users are advised to verify with local authorities.
 */

export interface StampDutyRate {
  state: string;
  male: number; // percentage
  female: number; // percentage (rebate in some states)
  joint: number; // percentage (for joint male+female)
  registrationCharge: number; // percentage
  registrationCap?: number; // max registration fee in ₹ (some states cap it)
  notes?: string;
}

export const STAMP_DUTY_RATES: StampDutyRate[] = [
  { state: "Andhra Pradesh", male: 5, female: 5, joint: 5, registrationCharge: 0.5, notes: "Additional surcharge of 2% applies as transfer duty" },
  { state: "Arunachal Pradesh", male: 6, female: 6, joint: 6, registrationCharge: 1 },
  { state: "Assam", male: 8.25, female: 8.25, joint: 8.25, registrationCharge: 0 , notes: "Registration fees included in stamp duty" },
  { state: "Bihar", male: 6.5, female: 6.5, joint: 6.5, registrationCharge: 2 },
  { state: "Chhattisgarh", male: 5, female: 4, joint: 4.5, registrationCharge: 4 },
  { state: "Delhi", male: 6, female: 4, joint: 5, registrationCharge: 1, registrationCap: 30000 },
  { state: "Goa", male: 3.5, female: 3.5, joint: 3.5, registrationCharge: 1, notes: "Lower rates for properties under ₹50L" },
  { state: "Gujarat", male: 4.9, female: 4.9, joint: 4.9, registrationCharge: 1 },
  { state: "Haryana", male: 7, female: 5, joint: 6, registrationCharge: 0, notes: "Registration included; 2% additional surcharge" },
  { state: "Himachal Pradesh", male: 5, female: 5, joint: 5, registrationCharge: 2 },
  { state: "Jharkhand", male: 4, female: 4, joint: 4, registrationCharge: 3 },
  { state: "Karnataka", male: 5, female: 5, joint: 5, registrationCharge: 1, notes: "Additional cess of 2% in BBMP area" },
  { state: "Kerala", male: 8, female: 8, joint: 8, registrationCharge: 2 },
  { state: "Madhya Pradesh", male: 7.5, female: 7.5, joint: 7.5, registrationCharge: 3 },
  { state: "Maharashtra", male: 6, female: 5, joint: 5.5, registrationCharge: 1, registrationCap: 30000, notes: "1% metro cess in Mumbai; LBT abolished" },
  { state: "Manipur", male: 7, female: 7, joint: 7, registrationCharge: 0 },
  { state: "Meghalaya", male: 9.9, female: 9.9, joint: 9.9, registrationCharge: 0 },
  { state: "Mizoram", male: 5, female: 5, joint: 5, registrationCharge: 0 },
  { state: "Nagaland", male: 8.25, female: 8.25, joint: 8.25, registrationCharge: 0 },
  { state: "Odisha", male: 5, female: 3, joint: 4, registrationCharge: 2 },
  { state: "Punjab", male: 7, female: 5, joint: 6, registrationCharge: 1 },
  { state: "Rajasthan", male: 6, female: 5, joint: 5.5, registrationCharge: 1, notes: "DLC rates used as base in many areas" },
  { state: "Sikkim", male: 5, female: 5, joint: 5, registrationCharge: 1 },
  { state: "Tamil Nadu", male: 7, female: 7, joint: 7, registrationCharge: 4 },
  { state: "Telangana", male: 5, female: 5, joint: 5, registrationCharge: 0.5, notes: "Additional transfer duty of 1.5%" },
  { state: "Tripura", male: 5, female: 5, joint: 5, registrationCharge: 0 },
  { state: "Uttar Pradesh", male: 7, female: 6, joint: 6.5, registrationCharge: 1, notes: "Women get 1% rebate on stamp duty" },
  { state: "Uttarakhand", male: 5, female: 3.75, joint: 4.375, registrationCharge: 2 },
  { state: "West Bengal", male: 7, female: 7, joint: 7, registrationCharge: 1, notes: "Below ₹1 cr: 6% stamp duty" },
];

/** Rent agreement stamp duty: flat fee or percentage-based by state */
export interface RentStampDutyRate {
  state: string;
  method: "flat" | "percentage";
  flatFee?: number; // ₹
  percentage?: number; // of total rent for lease period
  notes: string;
}

export const RENT_STAMP_DUTY_RATES: RentStampDutyRate[] = [
  { state: "Maharashtra", method: "percentage", percentage: 0.25, notes: "0.25% of total rent + deposit, minimum ₹100. Leave & license: ₹1000 flat up to 60 months" },
  { state: "Delhi", method: "flat", flatFee: 100, notes: "₹100 for agreements up to 5 years. Above 5 years: 2% of average annual rent" },
  { state: "Karnataka", method: "percentage", percentage: 1, notes: "1% of total rent for lease period; ₹200 for < 12 months" },
  { state: "Tamil Nadu", method: "percentage", percentage: 1, notes: "1% of total rent for lease period" },
  { state: "Telangana", method: "percentage", percentage: 0.4, notes: "0.4% of total rent + deposit" },
  { state: "Uttar Pradesh", method: "flat", flatFee: 100, notes: "₹100 for agreements up to 5 years" },
  { state: "Gujarat", method: "flat", flatFee: 100, notes: "₹100 for standard rental agreements" },
  { state: "West Bengal", method: "percentage", percentage: 0.25, notes: "0.25% of total rent for the period" },
  { state: "Kerala", method: "percentage", percentage: 1, notes: "1% of annual rent, minimum ₹100" },
  { state: "Rajasthan", method: "flat", flatFee: 100, notes: "₹100 for agreements up to 5 years" },
  { state: "Punjab", method: "flat", flatFee: 100, notes: "₹100 for standard rental agreements" },
  { state: "Haryana", method: "flat", flatFee: 100, notes: "₹100 for agreements up to 5 years" },
];

/** Capital gains indexation table (CII) */
export const COST_INFLATION_INDEX: Record<string, number> = {
  "2001-02": 100,
  "2002-03": 105,
  "2003-04": 109,
  "2004-05": 113,
  "2005-06": 117,
  "2006-07": 122,
  "2007-08": 129,
  "2008-09": 137,
  "2009-10": 148,
  "2010-11": 167,
  "2011-12": 184,
  "2012-13": 200,
  "2013-14": 220,
  "2014-15": 240,
  "2015-16": 254,
  "2016-17": 264,
  "2017-18": 272,
  "2018-19": 280,
  "2019-20": 289,
  "2020-21": 301,
  "2021-22": 317,
  "2022-23": 331,
  "2023-24": 348,
  "2024-25": 363,
  "2025-26": 377,
};

/** Indian states list for dropdowns */
export const INDIAN_STATES = STAMP_DUTY_RATES.map((r) => r.state).sort();

/** Format number as Indian rupees */
export function formatINR(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Format number with Indian comma grouping */
export function formatNumber(n: number): string {
  return new Intl.NumberFormat("en-IN").format(Math.round(n));
}
